/**
 * Ingesta por imagen (§4.5): contrato de salida, parseo estricto y borradores editables.
 *
 * Regla dura: **nada de lo que sale de una imagen escribe en la base sin confirmación del
 * usuario.** Este archivo no guarda nada. Convierte la respuesta del modelo en un borrador
 * precargado, marca lo que hay que revisar y deja el JSON crudo intacto para auditar.
 *
 * Las dos fuentes tienen dificultad muy distinta y el código lo refleja:
 * - Balanza: interfaz digital limpia, alta fiabilidad.
 * - Consola de cardio: siete segmentos, de noche, en ángulo, con reflejos. El 8 y el 0, el
 *   3 y el 9 se confunden seguido. Por eso el umbral de revisión es más exigente y por eso
 *   se pregunta siempre el nivel de resistencia, que no sale en la foto.
 */

import type { Condiciones, Fecha, MedidaCorporal, RegistroCardio, Unidad } from './types.ts';
import { validarCardio, validarMedida, type Resultado } from './validacion.ts';

export type TipoIngesta = 'balanza' | 'consola_cardio';

export interface CampoOCR {
  valor: number | null;
  confianza: number;
}

export interface LecturaOCR {
  tipo: TipoIngesta;
  confianza_global: number;
  campos: Record<string, CampoOCR>;
  /** Campos que el modelo NO pudo leer. Decirlo es obligatorio; inventar está prohibido. */
  no_legibles: string[];
}

/** Por debajo de esto el campo se resalta en la UI para revisión (§4.5). */
export const UMBRAL_CONFIANZA_BALANZA = 0.9;
/** Más exigente: un display de siete segmentos fotografiado de noche se lee mal seguido. */
export const UMBRAL_CONFIANZA_CARDIO = 0.95;

export function umbralConfianza(tipo: TipoIngesta): number {
  return tipo === 'balanza' ? UMBRAL_CONFIANZA_BALANZA : UMBRAL_CONFIANZA_CARDIO;
}

export const CAMPOS_BALANZA = [
  'peso_kg',
  'imc',
  'grasa_pct',
  'masa_grasa_kg',
  'masa_muscular_kg',
  'masa_magra_kg',
  'masa_osea_kg',
  'agua_pct',
  'grasa_visceral',
  'tmb_kcal',
  'edad_metabolica',
  'proteina_pct',
] as const;

export const CAMPOS_CARDIO = [
  'duracion_seg',
  'distancia_km',
  'calorias',
  'nivel_resistencia',
  'velocidad_kmh',
  'pulso',
] as const;

export function camposEsperados(tipo: TipoIngesta): readonly string[] {
  return tipo === 'balanza' ? CAMPOS_BALANZA : CAMPOS_CARDIO;
}

// ---------------------------------------------------------------------------
// Parseo estricto
// ---------------------------------------------------------------------------

export interface ParseoOCR {
  ok: boolean;
  lectura: LecturaOCR | null;
  error: string | null;
  /**
   * true si la respuesta venía envuelta en markdown. El contrato pide JSON pelado; se
   * acepta el bloque para no perder la lectura, pero queda registrado: es una señal de que
   * el prompt del modelo hay que apretarlo.
   */
  veniaEnMarkdown: boolean;
}

function esObjeto(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function aNumeroONull(x: unknown): number | null {
  if (x === null || x === undefined || x === '') return null;
  const n = typeof x === 'string' ? Number(x.replace(',', '.')) : x;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/**
 * Parsea la respuesta del modelo. Nunca lanza: devuelve `ok: false` con el motivo, porque
 * un modelo que responde mal es un caso esperado, no una excepción del programa.
 */
export function parsearLecturaOCR(texto: string, tipoEsperado?: TipoIngesta): ParseoOCR {
  const bruto = texto.trim();
  if (!bruto) return { ok: false, lectura: null, error: 'Respuesta vacía del modelo.', veniaEnMarkdown: false };

  const fence = bruto.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  const veniaEnMarkdown = fence !== null;
  const cuerpo = fence?.[1] ?? bruto;

  let datos: unknown;
  try {
    datos = JSON.parse(cuerpo);
  } catch {
    return {
      ok: false,
      lectura: null,
      error: 'El modelo no devolvió JSON válido. No se guarda nada.',
      veniaEnMarkdown,
    };
  }

  if (!esObjeto(datos)) {
    return { ok: false, lectura: null, error: 'El JSON no es un objeto.', veniaEnMarkdown };
  }

  const tipo = datos['tipo'];
  if (tipo !== 'balanza' && tipo !== 'consola_cardio') {
    return { ok: false, lectura: null, error: `Tipo desconocido: ${JSON.stringify(tipo)}.`, veniaEnMarkdown };
  }
  if (tipoEsperado && tipo !== tipoEsperado) {
    return {
      ok: false,
      lectura: null,
      error: `Se esperaba una lectura de ${tipoEsperado} y el modelo devolvió ${tipo}.`,
      veniaEnMarkdown,
    };
  }

  const camposCrudos = esObjeto(datos['campos']) ? datos['campos'] : {};
  const campos: Record<string, CampoOCR> = {};
  for (const [k, v] of Object.entries(camposCrudos)) {
    if (esObjeto(v)) {
      const valor = aNumeroONull(v['valor']);
      const confianza = aNumeroONull(v['confianza']);
      campos[k] = { valor, confianza: confianza === null ? 0 : Math.min(1, Math.max(0, confianza)) };
    } else {
      // Un modelo que devuelve `{"peso_kg": 97.5}` en vez de `{"valor":..,"confianza":..}`
      // no cumplió el contrato: se acepta el número pero con confianza 0, así cae en
      // revisión obligatoria en vez de pasar como dato bueno.
      campos[k] = { valor: aNumeroONull(v), confianza: 0 };
    }
  }

  const noLegiblesCrudo = datos['no_legibles'];
  const no_legibles = Array.isArray(noLegiblesCrudo)
    ? noLegiblesCrudo.filter((x): x is string => typeof x === 'string')
    : [];

  const global = aNumeroONull(datos['confianza_global']);
  const valores = Object.values(campos).map((c) => c.confianza);
  const confianza_global =
    global !== null
      ? Math.min(1, Math.max(0, global))
      : valores.length > 0
        ? valores.reduce((a, b) => a + b, 0) / valores.length
        : 0;

  return { ok: true, lectura: { tipo, confianza_global, campos, no_legibles }, error: null, veniaEnMarkdown };
}

/** Campos con confianza bajo el umbral: se resaltan para revisión (§4.5). */
export function camposParaRevisar(l: LecturaOCR): string[] {
  const umbral = umbralConfianza(l.tipo);
  return Object.entries(l.campos)
    .filter(([, c]) => c.valor === null || c.confianza < umbral)
    .map(([k]) => k)
    .sort();
}

// ---------------------------------------------------------------------------
// Borradores editables
// ---------------------------------------------------------------------------

export interface BorradorMedida {
  campos: Pick<
    MedidaCorporal,
    'peso' | 'grasa_pct' | 'masa_muscular' | 'masa_magra' | 'masa_osea' | 'agua_pct' | 'grasa_visceral' | 'imc' | 'tmb'
  >;
  unidad_peso: Unidad;
  /** Confianza por campo del borrador, ya traducida a los nombres del dominio. */
  confianzas: Partial<Record<keyof BorradorMedida['campos'], number>>;
  paraRevisar: (keyof BorradorMedida['campos'])[];
  noLegibles: string[];
  confianzaGlobal: number;
}

const MAPA_BALANZA: Record<string, keyof BorradorMedida['campos']> = {
  peso_kg: 'peso',
  grasa_pct: 'grasa_pct',
  masa_muscular_kg: 'masa_muscular',
  masa_magra_kg: 'masa_magra',
  masa_osea_kg: 'masa_osea',
  agua_pct: 'agua_pct',
  grasa_visceral: 'grasa_visceral',
  imc: 'imc',
  tmb_kcal: 'tmb',
};

export function aBorradorMedida(l: LecturaOCR): BorradorMedida {
  const campos: BorradorMedida['campos'] = {
    peso: 0,
    grasa_pct: null,
    masa_muscular: null,
    masa_magra: null,
    masa_osea: null,
    agua_pct: null,
    grasa_visceral: null,
    imc: null,
    tmb: null,
  };
  const confianzas: BorradorMedida['confianzas'] = {};
  const umbral = umbralConfianza(l.tipo);
  const paraRevisar = new Set<keyof BorradorMedida['campos']>();

  for (const [origen, destino] of Object.entries(MAPA_BALANZA)) {
    const c = l.campos[origen];
    if (!c) continue;
    if (destino === 'peso') campos.peso = c.valor ?? 0;
    else campos[destino] = c.valor;
    confianzas[destino] = c.confianza;
    if (c.valor === null || c.confianza < umbral) paraRevisar.add(destino);
  }

  // La masa magra puede venir implícita: si la balanza dio grasa en kg pero no magra.
  if (campos.masa_magra === null && campos.peso > 0) {
    const grasaKg = l.campos['masa_grasa_kg']?.valor ?? null;
    if (grasaKg !== null) {
      campos.masa_magra = Number((campos.peso - grasaKg).toFixed(2));
      confianzas.masa_magra = l.campos['masa_grasa_kg']?.confianza ?? 0;
      paraRevisar.add('masa_magra');
    }
  }

  return {
    campos,
    // La balanza reporta en kg; el contrato del modelo pide el peso en kg explícitamente.
    unidad_peso: 'kg',
    confianzas,
    paraRevisar: [...paraRevisar].sort(),
    noLegibles: l.no_legibles,
    confianzaGlobal: l.confianza_global,
  };
}

export interface BorradorCardio {
  campos: Pick<RegistroCardio, 'duracion_seg' | 'distancia_km' | 'calorias' | 'nivel_resistencia'>;
  confianzas: Partial<Record<keyof BorradorCardio['campos'], number>>;
  paraRevisar: (keyof BorradorCardio['campos'])[];
  noLegibles: string[];
  confianzaGlobal: number;
  /**
   * §4.5.5 — el nivel de resistencia no aparece en la foto y sin él los datos no son
   * comparables entre sesiones. Siempre hay que preguntarlo salvo que el modelo lo haya
   * leído de verdad.
   */
  pedirNivelResistencia: boolean;
}

export function aBorradorCardio(l: LecturaOCR): BorradorCardio {
  const umbral = umbralConfianza(l.tipo);
  const paraRevisar = new Set<keyof BorradorCardio['campos']>();
  const confianzas: BorradorCardio['confianzas'] = {};

  const leer = (clave: string, destino: keyof BorradorCardio['campos']): number | null => {
    const c = l.campos[clave];
    if (!c) {
      paraRevisar.add(destino);
      return null;
    }
    confianzas[destino] = c.confianza;
    if (c.valor === null || c.confianza < umbral) paraRevisar.add(destino);
    return c.valor;
  };

  const duracion = leer('duracion_seg', 'duracion_seg');
  const distancia = leer('distancia_km', 'distancia_km');
  const calorias = leer('calorias', 'calorias');
  const nivel = leer('nivel_resistencia', 'nivel_resistencia');

  return {
    campos: {
      duracion_seg: duracion ?? 0,
      distancia_km: distancia ?? 0,
      calorias: calorias ?? 0,
      nivel_resistencia: nivel,
    },
    confianzas,
    paraRevisar: [...paraRevisar].sort(),
    noLegibles: l.no_legibles,
    confianzaGlobal: l.confianza_global,
    pedirNivelResistencia: nivel === null,
  };
}

// ---------------------------------------------------------------------------
// Validación del borrador antes de mostrarlo (§4.5.2)
// ---------------------------------------------------------------------------

export function validarBorradorMedida(
  b: BorradorMedida,
  previa?: { peso: number; unidad_peso: Unidad } | null,
): Resultado {
  return validarMedida(
    { ...b.campos, unidad_peso: b.unidad_peso },
    previa
      ? {
          peso: previa.peso,
          unidad_peso: previa.unidad_peso,
          grasa_pct: null,
          masa_magra: null,
          masa_muscular: null,
          masa_osea: null,
          agua_pct: null,
          grasa_visceral: null,
          imc: null,
          tmb: null,
        }
      : null,
  );
}

export function validarBorradorCardio(b: BorradorCardio): Resultado {
  return validarCardio(b.campos);
}

// ---------------------------------------------------------------------------
// Prompts — el contrato vive junto a su validador
// ---------------------------------------------------------------------------

const REGLAS_COMUNES = `
Devolvés EXCLUSIVAMENTE un objeto JSON. Sin prosa, sin explicaciones, sin bloques de
markdown, sin \`\`\`.

Forma exacta:
{"tipo": "...", "confianza_global": 0.0, "campos": {"nombre": {"valor": 0, "confianza": 0.0}}, "no_legibles": []}

Reglas que no se negocian:
- "confianza" es tu certeza real de haber leído ESE dígito, de 0 a 1. No la infles.
- Si un campo no está en la imagen o no lo podés leer con seguridad, NO lo inventes:
  ponelo en "no_legibles" y omitilo de "campos", o mandá {"valor": null, "confianza": 0}.
- Un número plausible inventado es peor que un campo faltante: el faltante se le pregunta
  al usuario, el inventado se guarda como si fuera verdad.
- Nunca corrijas ni "redondees" lo que ves. Transcribí.
`.trim();

export const PROMPT_BALANZA = `
Leés la pantalla de una balanza inteligente de composición corporal.

${REGLAS_COMUNES}

tipo: "balanza"

Campos posibles (usá exactamente estos nombres, sólo los que veas):
- peso_kg           peso corporal en kilos
- imc               índice de masa corporal
- grasa_pct         porcentaje de grasa corporal
- masa_grasa_kg     masa grasa en kilos
- masa_muscular_kg  masa muscular en kilos
- masa_magra_kg     masa magra / libre de grasa en kilos
- masa_osea_kg      masa ósea en kilos
- agua_pct          porcentaje de agua corporal
- grasa_visceral    índice de grasa visceral (número entero, típicamente 1–30)
- tmb_kcal          metabolismo basal en kcal
- edad_metabolica   en años
- proteina_pct      porcentaje de proteína

Si la pantalla muestra el peso en libras, convertilo a kilos y bajá la confianza de
peso_kg a 0.8 como máximo, porque estás transformando el dato.
`.trim();

export const PROMPT_CARDIO = `
Leés la consola de una máquina de cardio: display de siete segmentos, probablemente
fotografiado de noche, en ángulo y con reflejos.

${REGLAS_COMUNES}

tipo: "consola_cardio"

Campos posibles (usá exactamente estos nombres, sólo los que veas):
- duracion_seg       tiempo TOTAL en segundos (si ves "30:34", son 1834)
- distancia_km       distancia en kilómetros
- calorias           calorías
- nivel_resistencia  nivel de resistencia o inclinación, si está visible
- velocidad_kmh      velocidad instantánea, si está visible
- pulso              pulsaciones, si están visibles

Cuidados específicos de los siete segmentos:
- El 8 y el 0 se confunden cuando falla un segmento; el 3 y el 9 también. Si dudás entre
  dos dígitos, elegí el más probable y bajá la confianza a 0.6 o menos. No adivines en
  silencio.
- Si la unidad de distancia dice MILES / MI, convertí a kilómetros y bajá la confianza.
- El nivel de resistencia normalmente NO está en la pantalla de resumen. Si no lo ves,
  ponelo en "no_legibles". No lo estimes a partir de la velocidad.
`.trim();

export function promptPara(tipo: TipoIngesta): string {
  return tipo === 'balanza' ? PROMPT_BALANZA : PROMPT_CARDIO;
}

// ---------------------------------------------------------------------------
// Trazabilidad
// ---------------------------------------------------------------------------

export interface RegistroIngesta {
  tipo: TipoIngesta;
  jsonCrudo: string;
  confianza: number;
  fecha: Fecha;
  condiciones?: Condiciones;
}
